import { Injectable } from '@angular/core';
import { AuthService } from '@auth0/auth0-angular';

// Centralises "the session is no longer usable → send the user to log in".
// Used by both the token interceptor (couldn't obtain an access token) and the
// error interceptor (API returned 401). Keeps a single loop-guard so the two
// paths can't bounce the browser between the app and Auth0.
@Injectable({ providedIn: 'root' })
export class AuthRedirectService {
  private static readonly REDIRECT_AT_KEY = 'auth401RedirectAt';
  private static readonly ACCESS_DENIED_KEY = 'authAccessDenied';

  private signingOut = false;

  // Public / unauthenticated endpoints where an auth failure must NOT force login.
  private readonly publicUrlFragments = [
    '/customer/onboard',
    '/customer/complete-onboarding',
    '/onboarding/verify/',
    '/onboarding/complete/',
    '/pentest/request-public',
  ];

  constructor(private auth: AuthService) {}

  isPublicUrl(url: string): boolean {
    return this.publicUrlFragments.some((fragment) => url.includes(fragment));
  }

  // The API refused this identity (never invited, or the invitation expired). End the
  // Auth0 session and drop the cached tokens so nothing of the sign-in survives: a reload
  // or a new tab starts at the login screen, and the next sign-in can pick another
  // account. Auth0 returns to the root, where the marker routes this tab to
  // /no-permission once. sessionStorage survives the round trip in this tab only.
  // Concurrent refusals all land here; only the first one signs out.
  signOutAsAccessDenied(): void {
    if (this.signingOut) {
      return;
    }
    this.signingOut = true;
    try {
      sessionStorage.setItem(AuthRedirectService.ACCESS_DENIED_KEY, '1');
    } catch {
      // Storage blocked: the sign-out still happens, they just land on the login screen.
    }
    this.auth
      .logout({ logoutParams: { returnTo: window.location.origin } })
      .subscribe({ error: () => undefined });
  }

  // True once after signOutAsAccessDenied() came back from Auth0 in this tab.
  static consumeAccessDenied(): boolean {
    try {
      const denied =
        sessionStorage.getItem(AuthRedirectService.ACCESS_DENIED_KEY) === '1';
      sessionStorage.removeItem(AuthRedirectService.ACCESS_DENIED_KEY);
      return denied;
    } catch {
      return false;
    }
  }

  // Redirect to Auth0 to obtain a fresh session/token, discarding the stale cached
  // one. Completes silently when the Auth0 session is still valid, otherwise shows
  // the login page. Returns false (and does nothing) when a redirect is unsafe:
  //  - on the /integrations OAuth callback (Auth0 owns that navigation), or
  //  - within 20s of a previous redirect, so a persistent 401 (e.g. an audience
  //    mismatch that survives re-login) can't loop the browser forever.
  redirectToLogin(): boolean {
    if (window.location.pathname.startsWith('/integrations')) {
      return false;
    }
    const lastRedirect = Number(
      sessionStorage.getItem(AuthRedirectService.REDIRECT_AT_KEY) || 0,
    );
    if (Date.now() - lastRedirect < 20000) {
      return false;
    }
    sessionStorage.setItem(
      AuthRedirectService.REDIRECT_AT_KEY,
      String(Date.now()),
    );
    const target = window.location.pathname + window.location.search;
    this.auth
      .loginWithRedirect({ appState: { target } })
      .subscribe({ error: () => {} });
    return true;
  }
}
